// Modèle métier du moteur Projet / Chantier (Phase 2) — constantes, permissions, calculs purs
export const ROLES = {
  partenaire: { label: "Partenaire", color: "#284E55" },
  entreprise: { label: "Entreprise", color: "#8A5541" },
  client: { label: "Client", color: "#3C4D3A" },
  central: { label: "CozyHome Central", color: "#292D2B" },
};

export const LOT_CATALOG = [
  "Démolition", "Maçonnerie", "Charpente", "Couverture", "Menuiseries extérieures", "Plomberie", "Électricité",
  "Chauffage", "Isolation", "Plâtrerie", "Chapes", "Carrelage", "Sols", "Peinture", "Cuisine", "Sanitaires", "Aménagements extérieurs",
];

export const QUOTE_STATUS = {
  depose: "Devis déposé", a_examiner: "À examiner", retenu: "Retenu", accepte: "Accepté / signé", preparation: "Préparation intervention", refuse: "Non retenu",
};
export const INVOICE_TYPES = { acompte: "Acompte", situation: "Situation", solde: "Solde" };
export const INVOICE_STATUS = {
  a_verifier: "À vérifier", retournee: "Retournée à l'entreprise", a_regler: "À régler", paiement_declare: "Paiement déclaré par le client",
  paiement_confirme: "Paiement confirmé", contestee: "Contestée / À examiner",
};
export const OBS_STATUS = {
  publiee: "Photo", signale: "Signalé", a_traiter: "Transmis à l'entreprise", en_cours: "Pris en charge", a_verifier: "Réalisé", cloture: "Clos",
};
export const DOC_STATUS = { a_fournir: "À fournir", en_verification: "En vérification", valide: "Valide", refuse: "Refusé", expire: "Expiré" };
export const PROGRESS_STATUS = { a_verifier: "À vérifier", validee: "Validée", refusee: "Correction demandée" };
export const CONTEST_REASONS = ["Travaux non terminés", "Montant différent du devis", "Prestation à vérifier", "Autre"];
export const PAYMENT_MODES = ["Virement", "Chèque", "Carte bancaire", "Autre"];
export const CENTRAL_STATUS = {
  commande: "Commande", infos: "Informations complètes", attente_paiement: "En attente de paiement", paye: "Paiement confirmé",
  a_produire: "À produire", production: "En production", a_verifier: "À vérifier", livree: "Livrée",
};
export const CENTRAL_FLOW = ["commande", "infos", "attente_paiement", "paye", "a_produire", "production", "a_verifier", "livree"];

// Types de documents entreprise — les exigences par métier restent PARAMÉTRABLES (requiredDocs sur chaque entreprise)
export const COMPANY_DOC_TYPES = {
  kbis: "Kbis",
  rcpro: "Attestation RC Professionnelle",
  decennale: "Attestation décennale",
  urssaf: "Attestation de vigilance URSSAF",
  qualif: "Qualification / certification métier",
};

// Matrice de permissions — à répliquer côté API lors de la connexion backend
export const PERMISSIONS = {
  partenaire: [
    "project.view_all", "quote.review", "quote.accept", "progress.validate", "invoice.validate", "invoice.return",
    "planning.edit", "observation.signal", "observation.report", "observation.assign", "observation.verify", "observation.close", "attestation.archive", "message.post",
    "contract.create", "contract.edit", "contract.send", "contract.view_own", "contract.amend",
  ],
  entreprise: [
    "project.view_own_lots", "quote.deposit", "progress.declare", "photo.add", "observation.treat", "observation.mark_done",
    "invoice.deposit", "payment.confirm", "document.upload", "message.post",
  ],
  client: ["project.view_validated", "payment.declare", "invoice.contest", "observation.signal", "observation.report", "message.post", "contract.view_own", "contract.sign", "contract.refuse"],
  central: ["document.verify", "service.produce"],
};
export const can = (role, action) => (PERMISSIONS[role] || []).includes(action);

export const euro = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
export const ttc = (ht, tva = 20) => Math.round(ht * (1 + tva / 100));
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");
export const addDays = (iso, d) => { const x = new Date(iso); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };
export const today = () => new Date().toISOString().slice(0, 10);

// Conformité documentaire d'une entreprise (bloquante pour l'intervention)
export const companyCompliance = (company) => {
  const missing = [];
  (company.requiredDocs || []).forEach((type) => {
    const versions = company.documents.filter((d) => d.type === type).sort((a, b) => b.version - a.version);
    const current = versions[0];
    if (!current || current.status !== "valide" || (current.validTo && current.validTo < today())) {
      missing.push({ type, label: COMPANY_DOC_TYPES[type], status: !current ? "a_fournir" : current.validTo < today() ? "expire" : current.status });
    }
  });
  return { ok: missing.length === 0, missing };
};

export const acceptedQuote = (project, lot) => project.quotes.find((q) => q.id === lot.quoteId && ["accepte", "preparation"].includes(q.status));

// Finances par lot : marché / avancement validé / déjà facturé / nouvelle situation / cumul / reste
export const lotFinance = (project, lot) => {
  const market = lot.amountHT || 0;
  const validatedInvoices = project.invoices.filter((i) => i.lotId === lot.id && ["a_regler", "paiement_declare", "paiement_confirme"].includes(i.status));
  const already = validatedInvoices.reduce((s, i) => s + i.amountHT, 0);
  const pending = project.invoices.filter((i) => i.lotId === lot.id && i.status === "a_verifier").reduce((s, i) => s + i.amountHT, 0);
  const facturable = Math.round((market * (lot.validatedProgress || 0)) / 100);
  const newSituation = Math.max(0, facturable - already - pending);
  return { market, already, pending, facturable, newSituation, cumul: already + pending, reste: market - already - pending };
};

// Avancement global pondéré (configurable : montant des lots ou pondération projet). Indicateur de progression physique.
export const weightedProgress = (project) => {
  const lots = project.lots.filter((l) => l.amountHT > 0);
  const w = (l) => (project.progressWeighting === "custom" ? l.weight || 1 : l.amountHT);
  const total = lots.reduce((s, l) => s + w(l), 0);
  if (!total) return 0;
  return Math.round(lots.reduce((s, l) => s + w(l) * (l.validatedProgress || 0), 0) / total);
};

// Interventions potentiellement impactées par le décalage d'un lot (dépendances transitives) — sans déplacement automatique
export const impactedLots = (project, lotId, newEnd) => {
  const out = [];
  const visit = (id) => {
    project.lots.filter((l) => (l.dependsOn || []).includes(id)).forEach((l) => {
      if (!out.find((x) => x.id === l.id)) {
        out.push({ ...l, conflict: l.start && newEnd && l.start <= newEnd });
        visit(l.id);
      }
    });
  };
  visit(lotId);
  return out;
};

export const lotIsLate = (lot) => lot.end && lot.end < today() && (lot.validatedProgress || 0) < 100 && lot.amountHT > 0;

export const nextRef = (partnerCode, year, counter) => `${partnerCode}-${String(year).slice(-2)}-${String(counter).padStart(3, "0")}`;
